/* src/components/admin/AssignDispatcherModal.tsx */
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { adminDispatcherZipcodeService } from "../../services/admin.dispatcher-zipcode.service";
import { AdminDispatcher } from "../../services/admin.dispatcher.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faUser } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import styles from "../../styles/components/DispatcherVehicleModal.module.css";
import modalStyles from "../../styles/common/Modal.module.css";

interface AssignDispatcherModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    idZone: number;
}

const customSelectStyles = {
    control: (base: any, state: any) => ({
        ...base,
        minHeight: "42px",
        borderRadius: "8px",
        borderColor: state.isFocused ? "var(--elPaket-primary-color)" : "#cbd5e1",
        boxShadow: state.isFocused ? "0 0 0 3px var(--elPaket-primary-color-light, rgba(232, 77, 48, 0.1))" : "none",
        "&:hover": {
            borderColor: state.isFocused ? "var(--elPaket-primary-color)" : "#cbd5e1",
        },
        fontSize: "14px",
        background: "white",
        paddingLeft: "30px",
    }),
    menu: (base: any) => ({
        ...base,
        borderRadius: "12px",
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        zIndex: 9999,
    }),
    option: (base: any, state: any) => ({
        ...base,
        fontSize: "14px",
        backgroundColor: state.isSelected 
            ? "var(--elPaket-primary-color)" 
            : state.isFocused 
                ? "#f1f5f9" 
                : "transparent",
        color: state.isSelected ? "var(--brand-black, #000000)" : "#475569",
        "&:active": {
            backgroundColor: "var(--elPaket-primary-color)",
            color: "var(--brand-black, #000000)",
        },
    }),
    menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
    }),
};

const AssignDispatcherModal: React.FC<AssignDispatcherModalProps> = ({
    show,
    onHide,
    onSuccess,
    idZone,
}) => {
    const [dispatchers, setDispatchers] = useState<AdminDispatcher[]>([]);
    const [selectedDispatcherId, setSelectedDispatcherId] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchAvailable = async () => {
        try {
            setLoading(true);
            const response = await adminDispatcherZipcodeService.getAvailableDispatchersForZone(idZone);
            if (response.statusCode === 200) {
                setDispatchers(response.data);
            }
        } catch (err) {
            console.error("Error fetching available dispatchers:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show && idZone) {
            fetchAvailable();
            setSelectedDispatcherId("");
        }
    }, [show, idZone]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDispatcherId) {
            Swal.fire({
                icon: "warning",
                title: "Advertencia",
                text: "Por favor, seleccione un transportista.",
            });
            return;
        }

        try {
            setSubmitting(true);
            const response = await adminDispatcherZipcodeService.assignDispatcherToZone({
                idDispatcher: Number(selectedDispatcherId),
                idZipcodeDispatchZone: idZone,
            });

            if (response.statusCode === 200) {
                await Swal.fire({
                    icon: "success",
                    title: "Asignado",
                    text: "El transportista ha sido asignado con éxito a esta zona.",
                });
                onSuccess();
                onHide();
            } else {
                errorService.showErrorAlert(response, "Error");
            }
        } catch (err) {
            console.error("Error assigning dispatcher:", err);
            errorService.showErrorAlert(err, "Error");
        } finally {
            setSubmitting(false);
        }
    };

    const options = dispatchers.map((d) => ({
        value: String(d.id),
        label: `${d.firstName} ${d.lastName} (${d.code || d.dni})`,
    }));

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            keyboard={false}
            className={styles.createDispatcherModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Asignar Transportista a la Zona
                </Modal.Title>
            </Modal.Header>
            
            <Form onSubmit={handleSubmit} id="assign-dispatcher-form" noValidate>
                <Modal.Body>
                    <p className={styles.modalSubtitle}>
                        Selecciona un transportista de la lista para asignarlo a esta zona de despacho.
                    </p>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Cargando transportistas disponibles...</p>
                        </div>
                    ) : dispatchers.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            No hay transportistas activos disponibles para asignar. Todos ya están asignados o no hay transportistas activos.
                        </div>
                    ) : (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                                Seleccionar Transportista
                            </Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} style={{ zIndex: 10 }} />
                                <Select
                                    value={options.find((o) => o.value === selectedDispatcherId) || null}
                                    onChange={(val) => setSelectedDispatcherId(val?.value || "")}
                                    options={options}
                                    placeholder="Seleccione un Transportista..."
                                    isSearchable
                                    isClearable
                                    styles={customSelectStyles}
                                    menuPortalTarget={document.body}
                                    noOptionsMessage={() => "No se encontraron transportistas"}
                                />
                            </div>
                        </Form.Group>
                    )}
                </Modal.Body>
                
                <Modal.Footer className={styles.modalFooter}>
                    <Button
                        variant="secondary"
                        onClick={onHide}
                        disabled={submitting}
                        className={styles.secondaryBtn}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        form="assign-dispatcher-form"
                        disabled={submitting || loading || dispatchers.length === 0}
                        className={modalStyles.closeBtn}
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Asignando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} className="me-2" />
                                Asignar
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AssignDispatcherModal;
